import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { REL_STATUSES, REL_PRIORITIES } from "./teamShared";
import { translateFr as tfr } from "@/i18n";

const SPORTS = ["football", "basketball", "handball", "rugby", "volleyball", "hockey", "tennis", "athletisme", "football_americain"];

export default function EditClubDialog({ club, open, onOpenChange, onSaved }) {
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (open && club) {
      setForm({
        name: club.name || "", sport: club.sport || "football", competition: club.competition || club.league || "",
        country: club.country || "", city: club.city || "", website: club.website || "",
        description: club.description || "", relationship_status: club.relationship_status || "",
        priority: club.priority || "", tags: (club.tags || []).join(", "),
      });
    }
  }, [open, club]);

  const submit = async () => {
    if (!form.name?.trim()) { toast.error(tfr("Le nom de l'équipe est requis")); return; }
    if (form.website && !/^https?:\/\/|^$/.test(form.website)) { toast.error("Le site web doit commencer par http(s)://"); return; }
    setBusy(true);
    try {
      await api.patch(`/clubs/${club.id}`, {
        name: form.name.trim(), sport: form.sport, competition: form.competition,
        country: form.country, city: form.city, website: form.website,
      });
      await api.patch(`/clubs/${club.id}/relationship`, {
        description: form.description,
        ...(form.relationship_status ? { relationship_status: form.relationship_status } : {}),
        ...(form.priority ? { priority: form.priority } : {}),
        tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
      });
      toast.success(tfr("Équipe mise à jour"));
      onOpenChange(false);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossible d'enregistrer les modifications.");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[88vh] overflow-y-auto" data-testid="edit-club-dialog">
        <DialogHeader><DialogTitle className="text-base">{tfr("Modifier l'équipe")}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><Label className="text-xs">{tfr("Nom de l'équipe")}</Label>
            <Input value={form.name || ""} onChange={(e) => set("name", e.target.value)} className="h-10 mt-1" data-testid="edit-club-name" /></div>
          <div><Label className="text-xs">Sport</Label>
            <Select value={form.sport} onValueChange={(v) => set("sport", v)}>
              <SelectTrigger className="h-10 mt-1 text-xs capitalize" data-testid="edit-club-sport"><SelectValue /></SelectTrigger>
              <SelectContent>{SPORTS.map((s) => <SelectItem key={s} value={s} className="capitalize">{s.replace("_", " ")}</SelectItem>)}</SelectContent>
            </Select></div>
          <div><Label className="text-xs">Niveau / championnat</Label>
            <Input value={form.competition || ""} onChange={(e) => set("competition", e.target.value)} placeholder="Ex. N3, Pro B…" className="h-10 mt-1" data-testid="edit-club-competition" /></div>
          <div><Label className="text-xs">Pays</Label>
            <Input value={form.country || ""} onChange={(e) => set("country", e.target.value)} className="h-10 mt-1" data-testid="edit-club-country" /></div>
          <div><Label className="text-xs">Ville</Label>
            <Input value={form.city || ""} onChange={(e) => set("city", e.target.value)} className="h-10 mt-1" data-testid="edit-club-city" /></div>
          <div className="col-span-2"><Label className="text-xs">Site web</Label>
            <Input value={form.website || ""} onChange={(e) => set("website", e.target.value)} placeholder="https://…" className="h-10 mt-1" data-testid="edit-club-website" /></div>
          <div className="col-span-2"><Label className="text-xs">Description</Label>
            <Textarea rows={2} value={form.description || ""} onChange={(e) => set("description", e.target.value)}
              placeholder={tfr("Contexte, spécialités, informations utiles…")} className="mt-1 resize-none" data-testid="edit-club-description" /></div>
          <div><Label className="text-xs">{tfr("Statut relation")}</Label>
            <Select value={form.relationship_status || undefined} onValueChange={(v) => set("relationship_status", v)}>
              <SelectTrigger className="h-10 mt-1 text-xs" data-testid="edit-club-relstatus"><SelectValue placeholder={tfr("Définir")} /></SelectTrigger>
              <SelectContent>{REL_STATUSES.map((s) => <SelectItem key={s} value={s}>{tfr(s)}</SelectItem>)}</SelectContent>
            </Select></div>
          <div><Label className="text-xs">{tfr("Priorité")}</Label>
            <Select value={form.priority || undefined} onValueChange={(v) => set("priority", v)}>
              <SelectTrigger className="h-10 mt-1 text-xs" data-testid="edit-club-priority"><SelectValue placeholder={tfr("Définir")} /></SelectTrigger>
              <SelectContent>{REL_PRIORITIES.map((s) => <SelectItem key={s} value={s}>{tfr(s)}</SelectItem>)}</SelectContent>
            </Select></div>
          <div className="col-span-2"><Label className="text-xs">{tfr("Tags (séparés par des virgules)")}</Label>
            <Input value={form.tags || ""} onChange={(e) => set("tags", e.target.value)} placeholder={tfr("formation, région lyonnaise…")} className="h-10 mt-1" data-testid="edit-club-tags" /></div>
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>{tfr("Annuler")}</Button>
          <Button onClick={submit} disabled={busy} className="bg-athlynx text-white hover:bg-athlynx/90" data-testid="edit-club-save">
            {busy ? "Enregistrement…" : "Enregistrer"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
